import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Centralised industry mode + UI profile.
 *
 * Why: tenants pick an industry ("restaurant" / "clothing" / ...) once. From
 * that moment the app should *look* like that industry — supermarket-only
 * fields disappear, the POS opens to the right first screen, and the sidebar
 * hides menus that don't apply. This context is the single place every page
 * reads from, so a switch propagates everywhere without prop drilling.
 *
 * The shape of `ui_profile` is defined by backend `get_ui_profile()` in
 * /app/backend/routers/industry.py — kept in sync there.
 */

// Safe defaults so pages render correctly even before the API responds.
// Mirrors backend `_DEFAULT_UI_PROFILE` (generic supermarket profile).
const DEFAULT_UI_PROFILE = {
  show_barcode: true,
  show_supplier: true,
  show_cost_price: true,
  show_min_stock: true,
  show_unit: true,
  show_box_packaging: true,
  show_wholesale_tiers: true,
  show_weighing: false,
  show_course_stage: false,
  show_kitchen_print_toggle: false,
  show_prep_time: false,
  show_recipe_modifiers: false,
  show_size_color_matrix: false,
  show_season: false,
  show_three_prices: false,
  show_batch_no: false,
  show_expiry_date: false,
  show_lot_tracking: false,
  show_prescription_flag: false,
  show_serial_no: false,
  show_warranty: false,
  show_appointment: false,
  show_service_duration: false,
  pos_first_screen: "categories",
  pos_show_tables: false,
  pos_show_queue_number: false,
  pos_show_split_bill: false,
  pos_show_kitchen_rush: false,
  pos_kitchen_print_on_send: false,
  menu_hide_transfers: false,
  menu_hide_wholesale: false,
  menu_hide_field_sales: false,
  menu_hide_box_management: false,
  menu_hide_apartados: false,
};

const IndustryContext = createContext({
  industry: "general",
  uiProfile: DEFAULT_UI_PROFILE,
  features: {},
  loading: false,
  reload: () => {},
});

export const useIndustry = () => useContext(IndustryContext);

export const IndustryProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [industry, setIndustry] = useState(() =>
    localStorage.getItem("cached_industry") || "general"
  );
  const [uiProfile, setUiProfile] = useState(() => {
    try {
      const cached = localStorage.getItem("cached_ui_profile");
      return cached ? { ...DEFAULT_UI_PROFILE, ...JSON.parse(cached) } : DEFAULT_UI_PROFILE;
    } catch {
      return DEFAULT_UI_PROFILE;
    }
  });
  const [features, setFeatures] = useState({});
  const [aiEnabled, setAiEnabled] = useState(() => localStorage.getItem("cached_ai_enabled") === "1");
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    // Super-admin (no tenant_id) doesn't have an industry — skip.
    if (!token || !user?.tenant_id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/industry/status`);
      const ind = r.data?.industry || "general";
      const profile = { ...DEFAULT_UI_PROFILE, ...(r.data?.ui_profile || {}) };
      setIndustry(ind);
      setUiProfile(profile);
      setFeatures(r.data?.features || {});
      const ai = !!r.data?.ai_enabled;
      setAiEnabled(ai);
      localStorage.setItem("cached_ai_enabled", ai ? "1" : "0");
      localStorage.setItem("cached_industry", ind);
      localStorage.setItem("cached_ui_profile", JSON.stringify(profile));
    } catch {
      /* offline → keep cached profile */
    } finally {
      setLoading(false);
    }
  }, [token, user?.tenant_id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const value = useMemo(
    () => ({ industry, uiProfile, features, aiEnabled, loading, reload }),
    [industry, uiProfile, features, aiEnabled, loading, reload]
  );

  return <IndustryContext.Provider value={value}>{children}</IndustryContext.Provider>;
};

/**
 * Conditional render helper.
 * Usage:
 *   <IfUI show="show_barcode"> ... </IfUI>
 *   <IfUI hide="show_box_packaging"> ... </IfUI>
 */
export const IfUI = ({ show, hide, children, fallback = null }) => {
  const { uiProfile } = useIndustry();
  if (show && !uiProfile[show]) return fallback;
  if (hide && uiProfile[hide]) return fallback;
  return children;
};

export default IndustryContext;
